// frontend/src/components/UpgradePrompt.tsx
//
// Displays an upgrade prompt when user doesn't have required tier to access an app
// Shows different messages based on monetization mode

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, RefreshCw, Check, Crown } from 'lucide-react';
import { SubscriptionTier, getTierDisplayName } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { PAYMENTS_DISABLED, PAYMENTS_DISABLED_MESSAGE } from '../lib/payments';

interface UpgradePromptProps {
  requiredTier: SubscriptionTier;
  appName?: string;
}

export function UpgradePrompt({ requiredTier, appName }: UpgradePromptProps) {
  const { user, subscriptionTier } = useAuth();
  const { syncSubscription, loading: syncLoading } = useSubscription();
  const [syncResult, setSyncResult] = useState<string | null>(null);
  
  console.log('[TIER_DEBUG] UpgradePrompt render:', {
    requiredTier,
    subscriptionTier,
    user: user?.id,
  });

  const handleSync = async () => {
    setSyncResult(null);
    const result = await syncSubscription();
    if (result) {
      if (result.previousTier !== result.newTier) {
        setSyncResult(`Subscription updated from ${result.previousTier} to ${result.newTier}. Reloading...`);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setSyncResult(`Subscription is already synced (${result.newTier})`);
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-6">
        <Crown className="h-8 w-8 text-amber-500" />
      </div>
      
      <h2 className="font-display text-2xl font-semibold text-theme-text mb-3">
        Premium App
      </h2>
      
      <p className="text-theme-text-secondary max-w-md mb-6">
        {appName ? (
          <>
            <span className="font-medium text-theme-text">{appName}</span> is available exclusively to paid subscribers.
          </>
        ) : (
          <>
            This app is available exclusively to paid subscribers.
          </>
        )}
      </p>

      {user ? (
        <div className="space-y-4">
          <p className="text-sm text-theme-text-muted">
            Your current plan: <span className="font-medium">{getTierDisplayName(subscriptionTier)}</span>
          </p>
          
          {syncResult && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-theme-success-bg border border-theme-success-border text-theme-success text-sm">
              <Check className="h-4 w-4" />
              {syncResult}
            </div>
          )}
          
          {PAYMENTS_DISABLED ? (
            <div className="max-w-md p-3 rounded-lg bg-theme-surface border border-theme-border-muted text-sm text-theme-text-secondary">
              {PAYMENTS_DISABLED_MESSAGE}
            </div>
          ) : (
            <Link 
              to="/pricing" 
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <Sparkles className="h-4 w-4" />
              View Plans
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
          
          <div className="pt-2 border-t border-theme-border-muted">
            <button
              onClick={handleSync}
              disabled={syncLoading}
              className="text-sm text-theme-text-muted hover:text-theme-text flex items-center gap-2 mx-auto"
            >
              <RefreshCw className={`h-4 w-4 ${syncLoading ? 'animate-spin' : ''}`} />
              {syncLoading ? 'Syncing...' : 'Already subscribed? Sync your subscription'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-theme-text-muted">
            Sign in or create an account to subscribe
          </p>
          <div className="flex gap-3">
            <Link 
              to={`/login?returnTo=${encodeURIComponent(window.location.pathname)}`}
              className="btn btn-secondary"
            >
              Sign In
            </Link>
            <Link 
              to={`/register?returnTo=${encodeURIComponent(window.location.pathname)}`}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              Create Account
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
